<script setup>
import { computed } from 'vue';
import ChatIcon from './ChatIcon.vue';
import ChatAvatar from './ChatAvatar.vue';
import { formatMessage } from '../helpers/formatMessage';

const props = defineProps({
  message: { type: Object, required: true },
  theme: { type: Object, required: true },
  // First/last of a run of messages from the same side, which decides corners and the avatar.
  groupStart: { type: Boolean, default: true },
  groupEnd: { type: Boolean, default: true },
  t: { type: Function, required: true },
});

defineEmits(['retry', 'mediaLoaded']);

const isVisitor = computed(() => props.message.kind === 'visitor');
const html = computed(() => formatMessage(props.message.content));
const avatar = computed(
  () => props.message.sender?.avatar || props.theme.avatar_url
);
const media = computed(() =>
  props.message.attachments.filter(file =>
    ['image', 'video', 'audio'].includes(file.file_type)
  )
);
const files = computed(() =>
  props.message.attachments.filter(
    file => !['image', 'video', 'audio'].includes(file.file_type)
  )
);

const bubbleClass = computed(() => {
  if (isVisitor.value) {
    return [
      'bg-primary text-on-primary rounded-ark',
      props.groupEnd ? 'rounded-br-md' : 'rounded-r-md',
    ];
  }
  return [
    'border border-line bg-surface text-ink rounded-ark',
    props.groupEnd ? 'rounded-bl-md' : 'rounded-l-md',
  ];
});
</script>

<template>
  <div
    v-if="message.kind === 'activity'"
    class="py-2 text-center text-[12px] text-muted"
  >
    {{ message.content }}
  </div>
  <template v-else>
    <div
      class="flex items-end gap-2 animate-bubble-in motion-reduce:animate-none"
      :class="[
        isVisitor ? 'justify-end pl-10' : 'pr-8',
        groupStart ? 'pt-2' : 'pt-0.5',
      ]"
    >
      <span v-if="!isVisitor" class="w-7 shrink-0">
        <ChatAvatar
          v-if="groupEnd"
          :src="avatar"
          :name="message.sender?.name || theme.title"
          size="xs"
        />
      </span>
      <!-- Text before media: flows caption a clip with "watch this 👇", so the words have to lead. -->
      <div
        class="flex min-w-0 max-w-full flex-col gap-1.5"
        :class="isVisitor ? 'items-end' : 'items-start'"
      >
        <div
          v-if="message.content"
          class="break-words px-3.5 py-2.5 text-[14.5px] leading-[1.45] transition-opacity"
          :class="[bubbleClass, message.pending ? 'opacity-70' : '']"
          v-html="html"
        />
        <div
          v-for="file in media"
          :key="file.id || file.data_url"
          class="overflow-hidden rounded-ark border border-line bg-black/5"
        >
          <img
            v-if="file.file_type === 'image'"
            :src="file.data_url"
            alt=""
            class="block max-h-72 w-full object-cover"
            loading="lazy"
            @load="$emit('mediaLoaded')"
          />
          <video
            v-else-if="file.file_type === 'video'"
            :src="`${file.data_url}#t=0.1`"
            controls
            playsinline
            preload="metadata"
            class="block max-h-80 w-full bg-black"
            :aria-label="t('VIDEO')"
            @loadedmetadata="$emit('mediaLoaded')"
          />
          <audio
            v-else
            :src="file.data_url"
            controls
            class="block w-64 max-w-full"
          />
        </div>
        <a
          v-for="file in files"
          :key="file.id || file.data_url"
          :href="file.data_url"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-2.5 rounded-ark border border-line bg-surface px-3.5 py-2.5 text-[13.5px] font-medium text-ink outline-none hover:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <ChatIcon name="file" class="h-5 w-5 text-primary" />
          {{ t('OPEN_FILE') }}
        </a>
        <button
          v-if="message.failed"
          type="button"
          class="flex items-center gap-1 text-[12px] font-medium text-danger outline-none hover:underline focus-visible:underline"
          @click="$emit('retry', message)"
        >
          <ChatIcon name="retry" class="h-3.5 w-3.5" />
          {{ t('RETRY') }}
        </button>
      </div>
    </div>
    <!-- A menu answered from the tray shows as the visitor's own reply, which is what it is. -->
    <div
      v-if="message.submitted"
      class="flex justify-end pl-10 pt-2 animate-bubble-in motion-reduce:animate-none"
    >
      <div
        class="rounded-ark rounded-br-md bg-primary px-3.5 py-2.5 text-[14.5px] leading-[1.45] text-on-primary"
      >
        {{ message.submitted.title }}
      </div>
    </div>
  </template>
</template>
